/** @format */
import { TFunction } from 'i18next';
import { omit, pick } from 'lodash';

import { AvailableMarket, MarketCode } from '@toucan/config/markets';

import { Model } from '@toucan/types/model';
import {
    GetSelectedWarrantyPolicyForProduct,
    ProductItemSummaryWithExtendedWarranty,
} from '@toucan/types/warranty';

import { getProductSummary } from '@toucan/services/requests/products';

import { centToDecimal, formatAsCurrency } from '@toucan/helpers/currency';
import { getFormattedDate } from '@toucan/helpers/date';

import { getModel } from '@toucan/client/core/model';
import { ShippingOption } from '@toucan/client/core/shipping';
import { TransactionMetadata } from '@toucan/client/features/transaction';
import {
    CalcServiceCustomerDetails,
    CalcServiceLineItemType,
    CalcServiceRequest,
    CalcServiceRequestItem,
    CalcServiceTaxCalculationInformation,
} from '@toucan/client/global/calculation-service/config/types';
import { ProductSummaryV2 } from '@toucan/client/global/product';
import {
    Address,
    Adjustment,
    CheckoutTransactionSummary,
    DeliverySummary,
    DeliveryType,
    DisputeItem,
    DisputeItemType,
    ItemObservation,
    ItemType,
    LineItemState,
    MixedTransaction,
    ProductItemSummary,
    PublicTransactionQuoteState,
    TransactionKind,
    TransactionState,
    TransactionSummary,
    TransactionSummary as TransactionSummaryData,
    TransactionVariant,
} from '@toucan/client/global/transaction';

export async function getTransactionMetadata(
    transaction: TransactionSummaryData,
    market: MarketCode,
    currentMetadata?: TransactionMetadata
): Promise<TransactionMetadata> {
    const reduceToNew =
        (idKey: string, metadataKey: string) =>
        (items: number[], item: ProductItemSummary) => {
            // @ts-ignore - Element implicitly has an any type - Touch and Change
            const id = item[idKey];

            // If id is 0, then its a manual model and we don't want to fetch it
            if (id === 0) {
                return items;
            }

            // @ts-ignore - Element implicitly has an any type - Touch and Change
            const isNew = !currentMetadata?.[metadataKey]?.find(
                (metadata: ProductSummaryV2 | Model) => metadata.id === id
            );

            if (isNew) {
                items.push(id);
            }

            return items;
        };

    const buyingProductIds: number[] = (transaction.itemsToBuy || []).reduce(
        reduceToNew('productId', 'products'),
        []
    );

    const sellingModelIds: number[] = (transaction.itemsToSell || []).reduce(
        reduceToNew('modelId', 'models'),
        []
    );

    const sellingProductIds: number[] = (transaction.itemsToSell || []).reduce(
        reduceToNew('productId', 'products'),
        []
    );

    const returningModelIds: number[] = (
        transaction.itemsToReturn || []
    ).reduce(reduceToNew('modelId', 'models'), []);

    const returningProductIds: number[] = (
        transaction.itemsToReturn || []
    ).reduce(reduceToNew('productId', 'products'), []);

    const productData: ProductSummaryV2[] = await Promise.all(
        [...buyingProductIds, ...sellingProductIds, ...returningProductIds].map(
            (id: number) => getProductSummary(id, market)
        )
    );

    const modelData: Model[] = await Promise.all(
        [...sellingModelIds, ...returningModelIds].map((id: number) =>
            getModel(id, market)
        )
    );

    return {
        products: productData,
        models: modelData,
    };
}

export const isTransactionSummary = (
    summary?: MixedTransaction
): summary is TransactionSummaryData => {
    return summary?.kind === TransactionKind.TRANSACTION_SUMMARY;
};

export const isTransactionCheckoutSummary = (
    summary?: MixedTransaction
): summary is CheckoutTransactionSummary => {
    return summary?.kind === TransactionKind.CHECKOUT_TRANSACTION_SUMMARY;
};

export function getCheckoutIdFromTransaction(
    transaction?: MixedTransaction
): string {
    if (isTransactionCheckoutSummary(transaction)) {
        return transaction.checkoutId;
    }

    if (isTransactionSummary(transaction)) {
        return transaction.checkout?.checkoutId ?? '';
    }

    return '';
}

export function getPromoCodeFromTransaction(transaction?: MixedTransaction) {
    if (!transaction) {
        return;
    }

    const checkoutPromotions = isTransactionSummary(transaction)
        ? transaction.checkout?.promotions
        : undefined;

    return (checkoutPromotions ?? transaction?.promotions)?.[0];
}

export function isTransactionOfVariantType(
    transaction: MixedTransaction | undefined,
    variant: TransactionVariant
): boolean {
    if (!isTransactionSummary(transaction)) {
        return false;
    }
    return transaction.variant === variant;
}

export const isTransactionOnCorrectMarket = (
    transaction: MixedTransaction,
    activeMarket: MarketCode
): boolean => {
    return transaction.market.toLowerCase() === activeMarket.toLowerCase();
};

/**
 * If the payment on the transaction has been completed
 * @param transaction The transaction to check
 */
export const hasTransactionBeenCompleted = (
    transaction: MixedTransaction
): boolean => {
    if (isTransactionSummary(transaction)) {
        const hasTotalPayment = transaction.totals.totalPayments > 0;
        const hasBillingAddress =
            transaction.billingAddress?.id !== undefined &&
            transaction.billingAddress?.id !== 0;

        return hasTotalPayment || hasBillingAddress;
    }

    return false;
};

export const isTransactionInPayableState = (
    transaction: MixedTransaction
): boolean => {
    if (!isTransactionSummary(transaction)) {
        // Is basket checkout
        return true;
    }

    const payableStates = [
        TransactionState.AWAITING_DELIVERY_CONFIG,
        TransactionState.DECLINED,
    ];

    const isAdjustedQuote =
        transaction?.initialQuote?.state ===
        PublicTransactionQuoteState.ADJUSTED;

    if (isAdjustedQuote) {
        payableStates.push(TransactionState.ADJUSTED_QUOTE_SENT);
    }

    const isOutboundPayment = transaction.totals.totalIncTax > 0;

    if (transaction.totals.balance !== 0) {
        if (isOutboundPayment) {
            payableStates.push(TransactionState.OK_TO_PAY);
        } else {
            // Inbound Payment
            payableStates.push(TransactionState.AWAITING_PAYMENT);
        }
    }

    return payableStates.includes(transaction.state);
};

const getUserInputDelivery = (transaction: MixedTransaction) => {
    const delivery = transaction.deliveries?.[0];

    const isUserInputDelivery =
        delivery?.deliveryType &&
        ![DeliveryType.SHIPMENT, DeliveryType.COLLECTION].includes(
            delivery.deliveryType
        );

    if (isUserInputDelivery) {
        return delivery;
    }

    return null;
};

const getShipmentFromTransaction = (transaction?: MixedTransaction) => {
    return (transaction as TransactionSummary)?.shipments?.find(
        (delivery) => delivery.deliveryType === DeliveryType.SHIPMENT
    );
};

const getCollectionFromTransaction = (transaction: MixedTransaction) => {
    return (transaction as TransactionSummary)?.collections?.find(
        (delivery) => delivery.deliveryType === DeliveryType.COLLECTION
    );
};

export const getShippingChargeForCalcService = (
    transaction: MixedTransaction
): number | undefined => {
    const delivery = (() => {
        const userInputDelivery = getUserInputDelivery(transaction);

        if (userInputDelivery) {
            return userInputDelivery;
        }

        switch (transaction.variant) {
            case TransactionVariant.ORDER:
            case TransactionVariant.PART_EXCHANGE:
            case TransactionVariant.PART_REFUND:
            case TransactionVariant.RETURN:
                return getShipmentFromTransaction(transaction);
            case TransactionVariant.CASH_PURCHASE:
                return getCollectionFromTransaction(transaction);
            // Basket has no transaction variant
            default:
                return transaction.deliveries?.[0];
        }
    })();

    return delivery?.chargeItem?.value;
};

export const getShippingAddressForCalcService = (
    transaction: MixedTransaction,
    parentTransaction: TransactionSummary | undefined
): Address | undefined => {
    const delivery = (() => {
        const userInputDelivery = getUserInputDelivery(transaction);

        if (userInputDelivery) {
            return userInputDelivery;
        }

        switch (transaction.variant) {
            case TransactionVariant.ORDER:
            case TransactionVariant.PART_EXCHANGE:
                return getShipmentFromTransaction(transaction);
            case TransactionVariant.CASH_PURCHASE:
                return getCollectionFromTransaction(transaction);
            case TransactionVariant.PART_REFUND:
            case TransactionVariant.RETURN:
                return getShipmentFromTransaction(parentTransaction);
            // Basket has no transaction variant
            default:
                return transaction.deliveries?.[0];
        }
    })();

    return delivery?.address;
};

function getDisputesForCalcService(
    transaction: MixedTransaction,
    disputeItemType: DisputeItemType
): CalcServiceRequestItem[] {
    if (!isTransactionSummary(transaction)) {
        return [];
    }

    const getCalcDisputeType = (disputeType: DisputeItemType) => {
        switch (disputeType) {
            case DisputeItemType.WARRANTY:
                return CalcServiceLineItemType.WARRANTY_REFUND;
            case DisputeItemType.CHARGE:
            default:
                return CalcServiceLineItemType.CHARGE_PART_REFUND;
        }
    };

    return transaction.disputes
        .filter((dispute) => dispute.disputedItemType === disputeItemType)
        .map((dispute) => {
            return {
                type: getCalcDisputeType(dispute.disputedItemType),
                baseValue: dispute.value,
                taxInclusive: dispute.taxInclusive,
                remoteReference: dispute.id,
                state: dispute.state,
                observations: [],
                adjustments: [],
                attributes: [],
                priceModifiers: [],
            };
        });
}

/** Transforms a transaction into the request object needed for the calc service */
export const getCalcServiceRequestFromTransaction = (
    transaction: MixedTransaction,
    parentTransaction: TransactionSummary | undefined,
    activeMarket: MarketCode,
    getSelectedWarrantyPolicyForProduct?: GetSelectedWarrantyPolicyForProduct,
    promoCode?: string
): CalcServiceRequest => {
    const transactionSummary = transaction as TransactionSummary;
    const isReturnTransaction =
        transaction.variant === TransactionVariant.RETURN;

    const composeObservations = (observations?: ItemObservation[]) =>
        observations?.map((observation) => ({
            observationId: observation.observationId,
            tierId: observation.tierId,
            value: observation.adjustment,
        })) ?? [];

    const composeAdjustments = (adjustments?: Adjustment[]) =>
        adjustments?.map((adjustment) => ({
            ...adjustment,
            type: adjustment.adjustmentType,
        })) ?? [];

    const goodsOut = transaction.itemsToBuy.map(
        ({
            id,
            productId,
            baseValue,
            attributes,
            observations,
            adjustments,
            priceModifiers,
        }) => {
            return {
                baseValue,
                productId,
                remoteReference: id,
                observations: composeObservations(observations),
                adjustments: composeAdjustments(adjustments),
                type: CalcServiceLineItemType.GOODS_OUT,
                // Attributes & Price Modifiers aren't defined during the quote journey when adding a new goods out
                // so we should define them as an empty array so that the call to the calculation service doesn't fail
                // This is okay to do due to the base value being the list price.
                attributes: attributes ?? [],
                priceModifiers: priceModifiers ?? [],
            };
        }
    );

    const goodsIn = isTransactionSummary(transaction)
        ? transaction.itemsToSell.map(
              ({
                  id,
                  baseValue,
                  attributes,
                  observations,
                  adjustments,
                  priceModifiers,
                  productId,
              }) => {
                  return {
                      baseValue,
                      attributes: attributes ?? [],
                      remoteReference: id,
                      observations: composeObservations(observations),
                      adjustments: composeAdjustments(adjustments),
                      priceModifiers: priceModifiers ?? [],
                      productId,
                      type: isReturnTransaction
                          ? CalcServiceLineItemType.RETURN
                          : CalcServiceLineItemType.GOODS_IN,
                  };
              }
          )
        : [];

    const warrantyItems = transaction.itemsToBuy.reduce<
        CalcServiceRequestItem[]
    >((warrantyItems, item: ProductItemSummaryWithExtendedWarranty) => {
        const warranty = getSelectedWarrantyPolicyForProduct?.(item.productId);

        if (warranty) {
            warrantyItems.push({
                type: CalcServiceLineItemType.WARRANTY,
                baseValue: warranty.value,
                productId: item.productId,
                observations: [],
                adjustments: [],
                attributes: [],
                priceModifiers: [],
            });
        }

        return warrantyItems;
    }, []);

    const warrantyDisputes = getDisputesForCalcService(
        transaction,
        DisputeItemType.WARRANTY
    );
    const chargeDisputesDisputes = getDisputesForCalcService(
        transaction,
        DisputeItemType.CHARGE
    );

    const promotions = [];

    if (promoCode) {
        promotions.push({
            code: promoCode,
        });
    }

    const charges = [];

    const shippingChargeValue = getShippingChargeForCalcService(transaction);

    const shippingAddress = getShippingAddressForCalcService(
        transaction,
        parentTransaction
    );

    if (shippingChargeValue) {
        charges.push({
            type: CalcServiceLineItemType.CHARGE,
            baseValue: shippingChargeValue,
            // Charge items are treated like line items in the calculation service, so we need to add the required line item properties to them.
            // A charge item can never have any of these adjustments so we can just send a blank array
            attributes: [],
            observations: [],
            adjustments: [],
            priceModifiers: [],
        });
    }

    const customerDetails: CalcServiceCustomerDetails = {};

    const emailAddress = transaction?.customerDetails?.emailAddress;
    if (emailAddress) {
        customerDetails.emailAddress = emailAddress;
    }

    if (shippingAddress) {
        customerDetails.address = omit(shippingAddress, 'id');

        const zipcode = customerDetails.address.zipcode;
        const state = customerDetails.address.state;

        // If the country is US we need to set the postcode and county before sending to the calc service if they are defined
        if (customerDetails.address.country === 'US' && zipcode && state) {
            customerDetails.address.postcode = zipcode;
            customerDetails.address.county = state;
        }
    }
    customerDetails.taxExemptionRef = transactionSummary?.taxExemptionReference;

    const lineItems = [
        ...goodsIn,
        ...goodsOut,
        ...warrantyItems,
        ...warrantyDisputes,
        ...charges,
        ...chargeDisputesDisputes,
    ];

    const taxCalculationInformation: CalcServiceTaxCalculationInformation = {
        market: activeMarket,
        customerId: transaction?.customerDetails?.customerId ?? 0,
    };

    if (isReturnTransaction) {
        taxCalculationInformation.parentCalculationDate = parentTransaction
            ? getLatestTaxAppliedDate(parentTransaction)
            : transactionSummary?.createdDate;
        taxCalculationInformation.refundableTax = Math.abs(
            parentTransaction?.totals?.totalTax ?? 0
        );
    }

    const transactionId =
        transactionSummary?.id ??
        (transaction as CheckoutTransactionSummary).transactionId;

    if (transactionId) {
        taxCalculationInformation.id = transactionId;
    }

    return {
        promotions,
        lineItems,
        customerDetails,
        taxCalculationInformation,
    };
};

export function sumTransactionItemValues(items: ProductItemSummary[]): number {
    return items.reduce((total: number, product: ProductItemSummary) => {
        return total + product.value;
    }, 0);
}

function sumDisputeItemValues(disputeItemType: DisputeItemType) {
    return function (disputes: DisputeItem[]): number {
        return disputes.reduce((total: number, dispute: DisputeItem) => {
            if (dispute.disputedItemType !== disputeItemType) {
                return total;
            }

            return total + dispute.value;
        }, 0);
    };
}

export const sumDeliveryChargeDisputeItemValues = sumDisputeItemValues(
    DisputeItemType.CHARGE
);

export const sumWarrantyDisputeItemValues = sumDisputeItemValues(
    DisputeItemType.WARRANTY
);

export const getShippingOption = (
    shipments: DeliverySummary[] = [],
    shippingOptions: ShippingOption[] = []
): ShippingOption | undefined =>
    shippingOptions.find(
        (option) => option.id === shipments?.[0]?.deliveryOption
    );

export const getShippingOptionByDeliveryOption = (
    deliveryOption: string | number | undefined,
    shippingOptions: ShippingOption[] = []
): ShippingOption | undefined =>
    shippingOptions.find((option) => option.id === deliveryOption);

/** Get the appropriate title of an item summary group as a string for a given transaction and a item type */
export function getSummaryGroupTitle(
    transaction: MixedTransaction,
    type:
        | ItemType.ITEMS_TO_BUY
        | ItemType.ITEMS_TO_SELL
        | ItemType.ITEMS_TO_RETURN,
    t: TFunction
): string {
    const isReturnTransaction = isTransactionOfVariantType(
        transaction,
        TransactionVariant.RETURN
    );

    switch (type) {
        case ItemType.ITEMS_TO_BUY:
            return isReturnTransaction
                ? t('Replacement items')
                : t("What you're buying");
        case ItemType.ITEMS_TO_SELL:
            return isReturnTransaction
                ? t("What you've returned")
                : t("What you're selling");
        case ItemType.ITEMS_TO_RETURN:
            return t('What MPB is returning to you');
    }
}

export function getMarketFromTransaction(
    transaction: TransactionSummary | undefined,
    availableMarkets: AvailableMarket[]
) {
    return availableMarkets.find(({ code }) => {
        return code === transaction?.market.toUpperCase();
    });
}

/**
 * Returns a value in the currency format of the specified market.
 * @param value The amount to format e.g. 123400
 * @returns The formatted value e.g. £1,234
 */
export function formatValueAsCurrency(
    value: number,
    market: { iso: string; currency: string }
) {
    const formatWithCurrency = formatAsCurrency(market.iso, market.currency);

    return formatWithCurrency(centToDecimal(value), { trailingZeros: false });
}

export function composeShippedDate(
    transaction: TransactionSummary,
    marketCode: MarketCode
): string {
    const { shipments = [] } = transaction;

    const shipment = shipments[0];

    return shipment?.shippedOn
        ? getFormattedDate(marketCode, shipment.shippedOn)
        : 'x';
}

/**
 * Gets the delivery address and creates a string from it
 */
export function composeDeliveryAddress(
    transaction: TransactionSummary
): string {
    const { shipments = [] } = transaction;

    const shipment = shipments[0];

    const address = pick(shipment?.address ?? {}, [
        'addressee',
        'line1',
        'line2',
        'town',
        'county',
        'state',
        'postcode',
        'zipcode',
        'country',
    ]);

    return Object.values(address).filter(Boolean).join(', ');
}

export function withAllowedSellState(
    transaction: MixedTransaction | undefined
): MixedTransaction | undefined {
    if (!transaction) {
        return transaction;
    }

    const transactionSummary = transaction as TransactionSummary;

    if (!transactionSummary?.itemsToSell) {
        return transaction;
    }

    return {
        ...transactionSummary,
        itemsToSell: [
            ...transactionSummary.itemsToSell.filter(
                ({ state }: { state: LineItemState }) =>
                    state !== LineItemState.NOT_RECEIVED
            ),
        ],
    };
}

export function withInStockProducts(unavailableProducts: number[]) {
    return function filterInStockProducts(
        transaction: TransactionSummary | undefined
    ): TransactionSummary | undefined {
        if (!transaction) {
            return;
        }

        return {
            ...transaction,
            itemsToBuy: transaction.itemsToBuy.filter(
                ({ productId }) => !unavailableProducts.includes(productId)
            ),
        };
    };
}

export function withDeliveryChargeItem<T extends MixedTransaction | undefined>(
    transaction: T,
    shippingOption?: ShippingOption
): T {
    if (!transaction || !shippingOption) {
        return transaction;
    }

    const mapDeliveries = (delivery: DeliverySummary) =>
        `${delivery.deliveryOption}` === `${shippingOption.id}`
            ? {
                  ...delivery,
                  chargeItem: {
                      value: shippingOption.value,
                      ...delivery.chargeItem,
                  },
              }
            : delivery;

    const deliveries = transaction.deliveries?.map(mapDeliveries) ?? [];
    const shipments = isTransactionSummary(transaction)
        ? transaction.shipments?.map(mapDeliveries) ?? []
        : undefined;

    return { ...transaction, deliveries, shipments };
}

export function getLatestTaxAppliedDate(transaction: TransactionSummary) {
    const { tax: taxes = [], createdDate } = transaction;

    if (!taxes.length) {
        return createdDate;
    }

    const latestTaxObject = taxes
        .filter((tax) => Boolean(tax.appliedDate))
        .sort(
            (a, b) =>
                new Date(a.appliedDate).getTime() -
                new Date(b.appliedDate).getTime()
        )
        .pop();

    return latestTaxObject?.appliedDate ?? createdDate;
}
